import React, { useEffect, useState, useRef } from 'react';
import { Field, Form, Formik } from 'formik';
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import AddIcon from '@material-ui/icons/Add';
import DescriptionIcon from '@material-ui/icons/Description';
import MonetizationOnIcon from '@material-ui/icons/MonetizationOn';
import StorageIcon from '@material-ui/icons/Storage';
import CallSplitIcon from '@material-ui/icons/CallSplit';
import CurrencyTextField from '@unicef/material-ui-currency-textfield/dist/CurrencyTextField';
import { Autocomplete, createFilterOptions } from '@material-ui/lab';
import ProductSaleFormatModal from '../Common/ProductSaleFormatModal';
import ProductSaleFormatCard from '../Common/ProductSaleFormatCard';
import useApi from '../../../utils/useApi';
import UIContainer from '../../UI/Container/UIContainer';
import UIFormTitle from '../../UI/Form/Title/UIFormTitle';
import validationSchema from './validationSchema';
import {
  calculateMarginByValue,
  calculateMarkupByValue,
  calculateProfitByValue,
  calculateValueByMargin,
  capitalize,
  clearObject,
  isEqualObject,
  formattedCurrency,
  formatStockAmount,
} from '../../../utils/utils';
import UIFormContainerSave from '../../UI/Form/ContainerSave/UIFormContainer';
import UIModalOldForm from '../../UI/ModalOldForm/UIModalOldForm';
import useStorage from '../../../utils/useStorage';
import SupplierCreateModal from '../../Supplier/SupplierCreateModal';
import UIImageUpload from '../../UI/ImageUpload/UIImageUpload';
import UIDuxiaDescriptionButton from '../../UI/Duxia/UIDuxiaDescriptionButton';
import ProductUnitSelect from '../Common/ProductUnitSelect';
import { isFractionalUnit, getUnitInputStep } from '../../../constants/productUnits';

const useStyles = makeStyles((theme) => ({
  tabsWrapper: {
    borderBottom: '1px solid #e2e8f0',
    backgroundColor: '#ffffff',
    borderRadius: '8px 8px 0 0',
    overflow: 'hidden',
  },
  tabRoot: {
    minHeight: 56,
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '0.875rem',
    padding: '6px 12px',
    [theme.breakpoints.down('xs')]: {
      minHeight: 48,
      minWidth: 0,
      padding: '6px 8px',
      fontSize: '0.75rem',
      flex: 1,
    },
  },
  tabLabel: {
    [theme.breakpoints.down('xs')]: {
      display: 'none',
    },
  },
  tabContent: {
    padding: theme.spacing(3),
    backgroundColor: '#ffffff',
    borderRadius: '0 0 8px 8px',
    boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
    border: '1px solid #e2e8f0',
    borderTop: 'none',
    minHeight: 360,
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2),
    },
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(1.5, 1),
    },
  },
  metricCard: {
    padding: theme.spacing(2),
    borderRadius: 8,
    backgroundColor: '#f8fafc',
    border: '1px solid #e2e8f0',
    marginBottom: theme.spacing(2),
  },
  marginHigh: {
    color: '#137333',
    fontWeight: 'bold',
  },
  marginLow: {
    color: '#d97706',
    fontWeight: 'bold',
  },
  profitBadgePositive: {
    backgroundColor: '#e6f4ea',
    color: '#137333',
    fontWeight: 'bold',
    padding: '4px 10px',
    borderRadius: 16,
    display: 'inline-flex',
    alignItems: 'center',
    fontSize: '0.875rem',
  },
  profitBadgeNegative: {
    backgroundColor: '#fce8e6',
    color: '#c5221f',
    fontWeight: 'bold',
    padding: '4px 10px',
    borderRadius: 16,
    display: 'inline-flex',
    alignItems: 'center',
    fontSize: '0.875rem',
  },
  infoCard: {
    padding: theme.spacing(2),
    borderRadius: 8,
    backgroundColor: '#f0f7ff',
    border: '1px solid #bbdefb',
    marginBottom: theme.spacing(2),
  },
  subSectionCard: {
    padding: theme.spacing(2),
    borderRadius: 8,
    border: '1px solid #e2e8f0',
    backgroundColor: '#fafbfc',
    marginTop: theme.spacing(1.5),
  },
  addFormatBtn: {
    fontWeight: 600,
    textTransform: 'none',
    borderRadius: 6,
    [theme.breakpoints.down('xs')]: {
      width: '100%',
      marginTop: theme.spacing(1),
    },
  },
  formatHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    marginBottom: theme.spacing(2),
    gap: theme.spacing(1),
  },
}));

const initialState = {
  name: '',
  description: '',
  category: null,
  supplier: null,
  unit: 'UN',
  cost: 0,
  value: 0,
  barCode: '',
  particulars: null,
  amount: 0,
  margin: 0,
  markup: 0,
  show_in_catalog: true,
  images: [],
};

const supplierFilter = createFilterOptions();

const ProductNew = () => {
  const classes = useStyles();
  const [oldValues, setOldValues] = useStorage('formProduct');
  const [open, setOpen] = useState(false);
  const [checked, setChecked] = useState(true);
  const [options, setOptions] = useState(null);
  const [costTotal, setCostTotal] = useState(0);
  const [isSaving, setIsSaving] = useState(false);

  const [tab, setTab] = useState(0);
  const [saleFormats, setSaleFormats] = useState([]);
  const [modalFormatOpen, setModalFormatOpen] = useState(false);
  const [editingFormatIndex, setEditingFormatIndex] = useState(null);

  const [modalSupplier, setModalSupplier] = useState(false);
  const [newSupplierName, setNewSupplierName] = useState('');

  const [registerLoad, registerInfo, setRegisterInfo] = useApi({
    method: 'post',
    url: '/products',
  });

  const handleChange = (event) => {
    setChecked(event.target.checked);
  };

  const [categoryLoad, categoryInfo] = useApi({
    method: 'get',
    url: '/products/categories',
  });

  const [suppliersLoad, suppliersInfo] = useApi({
    method: 'get',
    url: '/suppliers',
  });

  function changeModal() {
    setOpen(!open);
  }

  function setFormValues() {
    setOldValues(null);
    setOptions(null);
    changeModal();
  }

  function updateStorage(values) {
    setOldValues(values);
  }

  useEffect(() => {
    categoryLoad({ params: { isActive: 1 } });
    suppliersLoad();
    if (oldValues && !isEqualObject(oldValues, initialState)) {
      changeModal();
    }
  }, []);

  useEffect(() => {
    const handleTourStep = (e) => {
      const data = e.detail;
      if (!data) return;
      const index = data.index;
      if (typeof index === 'number') {
        if (index >= 3 && index <= 6) {
          setTab(3);
        } else if (index >= 0 && index < 3) {
          setTab(0);
        }
      }
    };
    window.addEventListener('joyride:step', handleTourStep);
    return () => window.removeEventListener('joyride:step', handleTourStep);
  }, []);

  function onSubmit(values, { resetForm }) {
    setIsSaving(true);
    const valuesProduct = {
      ...values,
      unit: values.unit || 'UN',
      category: values.category ? values.category.id : null,
      supplier: values.supplier ? values.supplier.id : null,
      show_in_catalog: Boolean(values.show_in_catalog),
      images: values.images || [],
      childFormats: saleFormats,
    };
    delete valuesProduct.margin;
    delete valuesProduct.markup;
    registerLoad({
      data: valuesProduct,
      onCompleted: (response) => {
        if (response) {
          resetForm({ values: initialState });
          setOldValues(null);
          setOptions(null);
          setCostTotal(0);
          setSaleFormats([]);
          setTab(0);
        }
        setIsSaving(false);
      },
      onError: () => {
        setIsSaving(false);
      },
    });
  }

  return (
    <>
      <Formik
        initialValues={oldValues || initialState}
        onSubmit={onSubmit}
        validationSchema={validationSchema}
      >
        {({ setFieldValue, errors, touched, values, submitCount, isValid }) => {
          const numCost = parseFloat(values.cost) || 0;
          const numValue = parseFloat(values.value) || 0;
          const currentMargin = parseFloat(calculateMarginByValue(numCost, numValue)) || 0;
          const currentProfit = calculateProfitByValue(numCost, numValue);
          const isProfitPositive = parseFloat(currentProfit) >= 0;

          const hasTab0Error = Boolean(
            (touched.name && errors.name) ||
            (touched.category && errors.category) ||
            (touched.supplier && errors.supplier) ||
            (touched.particulars && errors.particulars)
          );
          const hasTab1Error = Boolean(
            (touched.cost && errors.cost) ||
            (touched.value && errors.value)
          );
          const hasTab2Error = Boolean(
            touched.amount && errors.amount
          );

          // Auto-troca para a primeira aba com erro se o usuário tentou salvar
          const prevSubmitCount = useRef(submitCount);
          useEffect(() => {
            if (submitCount > prevSubmitCount.current && !isValid) {
              if (errors.name || errors.category || errors.supplier || errors.particulars) {
                setTab(0);
              } else if (errors.cost || errors.value) {
                setTab(1);
              } else if (errors.amount) {
                setTab(2);
              }
            }
            prevSubmitCount.current = submitCount;
          }, [submitCount, isValid, errors]);

          return (
            <Form onBlur={() => updateStorage(values)}>
              <UIContainer container direction="row">
                <Grid item xs={12} style={{ paddingBottom: 12 }}>
                  <UIFormTitle title="Novo produto" />
                </Grid>

                <Grid item xs={12}>
                  <Box className={classes.tabsWrapper}>
                    <Tabs
                      id="tour-product-tabs"
                      value={tab}
                      onChange={(e, val) => setTab(val)}
                      indicatorColor="primary"
                      textColor="primary"
                      variant="fullWidth"
                    >
                      <Tab
                        className={classes.tabRoot}
                        icon={
                          <Box position="relative" display="inline-flex">
                            <DescriptionIcon fontSize="small" />
                            {hasTab0Error && (
                              <span style={{
                                position: 'absolute', top: -4, right: -6,
                                width: 8, height: 8, borderRadius: '50%',
                                backgroundColor: '#d32f2f',
                              }} />
                            )}
                          </Box>
                        }
                        label={<span className={classes.tabLabel}>Dados Gerais</span>}
                      />
                      <Tab
                        className={classes.tabRoot}
                        icon={
                          <Box position="relative" display="inline-flex">
                            <MonetizationOnIcon fontSize="small" />
                            {hasTab1Error && (
                              <span style={{
                                position: 'absolute', top: -4, right: -6,
                                width: 8, height: 8, borderRadius: '50%',
                                backgroundColor: '#d32f2f',
                              }} />
                            )}
                          </Box>
                        }
                        label={<span className={classes.tabLabel}>Preços & Margem</span>}
                      />
                      <Tab
                        className={classes.tabRoot}
                        icon={
                          <Box position="relative" display="inline-flex">
                            <StorageIcon fontSize="small" />
                            {hasTab2Error && (
                              <span style={{
                                position: 'absolute', top: -4, right: -6,
                                width: 8, height: 8, borderRadius: '50%',
                                backgroundColor: '#d32f2f',
                              }} />
                            )}
                          </Box>
                        }
                        label={<span className={classes.tabLabel}>Estoque Inicial</span>}
                      />
                      <Tab
                        id="tour-product-formats-tab"
                        className={classes.tabRoot}
                        icon={
                          <Box position="relative" display="inline-flex">
                            <CallSplitIcon fontSize="small" />
                            {saleFormats.length > 0 && (
                              <span style={{
                                position: 'absolute', top: -4, right: -6,
                                minWidth: 16, height: 16, borderRadius: 8,
                                backgroundColor: '#0d47a1', color: '#fff',
                                fontSize: '0.6rem', fontWeight: 'bold',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                padding: '0 3px',
                              }}>{saleFormats.length}</span>
                            )}
                          </Box>
                        }
                        label={<span className={classes.tabLabel}>Formatos de Venda</span>}
                      />
                    </Tabs>
                  </Box>

                  <Paper className={classes.tabContent} elevation={0}>
                    {/* ABA 0: DADOS GERAIS */}
                    <Box style={{ display: tab === 0 ? 'block' : 'none' }}>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={7}>
                          <Field
                            name="name"
                            label="Nome do Produto"
                            error={Boolean(errors.name)}
                            helperText={touched.name && errors.name}
                            placeholder="Ex.: Whisky Cavalo Branco 1L, Caixa Cerveja Heineken c/24"
                            variant="outlined"
                            size="small"
                            fullWidth
                            as={TextField}
                          />
                        </Grid>
                        <Grid item xs={12} md={5} id="tour-product-category">
                          <Autocomplete
                            disableClearable
                            noOptionsText="Sem opções"
                            value={values.category}
                            disabled={categoryInfo.loading}
                            options={categoryInfo.data !== null ? categoryInfo.data : []}
                            getOptionLabel={(category) => category.name}
                            getOptionSelected={(option, value) => option.id === value.id}
                            onChange={(event, category) => {
                              if (event.target.value !== '' || category) {
                                setFieldValue('category', category);
                                setFieldValue(
                                  'particulars',
                                  clearObject(category.particulars, true),
                                );
                                setOptions({
                                  ...category.particulars,
                                });
                              } else {
                                setFieldValue('category', null);
                                setFieldValue('particulars', null);
                                setOptions(null);
                              }
                            }}
                            defaultValue={
                              oldValues !== null && oldValues.category
                                ? oldValues.category
                                : null
                            }
                            renderInput={(params) => (
                              <Field
                                {...params}
                                label="Categoria"
                                name="category"
                                error={Boolean(errors.category)}
                                helperText={touched.category && errors.category}
                                variant="outlined"
                                size="small"
                                placeholder="Selecione a categoria"
                                component={TextField}
                              />
                            )}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Autocomplete
                            disableClearable
                            noOptionsText="Sem opções"
                            value={values.supplier}
                            disabled={suppliersInfo.loading}
                            options={
                              suppliersInfo.data !== null ? suppliersInfo.data : []
                            }
                            getOptionLabel={(option) => {
                              if (typeof option === 'string') return option;
                              if (option && option.inputValue) return option.name;
                              return (option && option.name) || '';
                            }}
                            getOptionSelected={(option, value) => option.id === value.id}
                            filterOptions={(opts, params) => {
                              const filtered = supplierFilter(opts, params);
                              const existing = opts.some(
                                (option) =>
                                  option.name.toLowerCase() ===
                                  params.inputValue.toLowerCase(),
                              );
                              if (params.inputValue !== '' && !existing) {
                                filtered.push({
                                  inputValue: params.inputValue,
                                  name: `Adicionar "${params.inputValue}"`,
                                });
                              }
                              return filtered;
                            }}
                            onChange={(event, newValue) => {
                              if (!newValue) {
                                setFieldValue('supplier', null);
                                return;
                              }
                              if (newValue.inputValue) {
                                setNewSupplierName(newValue.inputValue);
                                setModalSupplier(true);
                              } else {
                                setFieldValue('supplier', newValue);
                              }
                            }}
                            defaultValue={
                              oldValues !== null && oldValues.supplier
                                ? oldValues.supplier
                                : null
                            }
                            renderInput={(params) => (
                              <Field
                                {...params}
                                label="Fornecedor"
                                name="supplier"
                                error={Boolean(errors.supplier)}
                                helperText={touched.supplier && errors.supplier}
                                variant="outlined"
                                size="small"
                                placeholder="Selecione ou crie um fornecedor"
                                component={TextField}
                              />
                            )}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Box display="flex" alignItems="center" gridGap={12}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={checked}
                                  onClick={handleChange}
                                  onChange={() => setFieldValue('barCode', '')}
                                  color="primary"
                                />
                              }
                              label="Automático"
                              style={{ marginRight: 0 }}
                            />
                            <Field
                              disabled={checked}
                              name="barCode"
                              label={checked ? 'Gerar código' : 'Código de barras'}
                              placeholder="Bipe com o leitor ou digite"
                              variant="outlined"
                              size="small"
                              fullWidth
                              as={TextField}
                            />
                          </Box>
                        </Grid>

                        {/* Particularidades da categoria */}
                        {values.particulars && options !== null && Object.keys(options).length > 0 && (
                          <Grid item xs={12}>
                            <Paper className={classes.subSectionCard} elevation={0}>
                              <Typography variant="subtitle2" style={{ fontWeight: 600, color: '#17497F', marginBottom: 12 }}>
                                Características da Categoria
                              </Typography>
                              <Grid container spacing={2}>
                                {Object.entries(options).map(([key, value]) => (
                                  <Grid key={key} item xs={12} sm={6} md={4}>
                                    <FormControl variant="outlined" size="small" fullWidth error={Boolean(errors.particulars && errors.particulars[key])}>
                                      <InputLabel id={`label-${key}`}>{capitalize(key)}</InputLabel>
                                      <Field
                                        name={`particulars.${key}`}
                                        disabled={!values.category}
                                        labelId={`label-${key}`}
                                        label={capitalize(key)}
                                        as={Select}
                                      >
                                        <MenuItem disabled value="">
                                          <em>Informe o(a) {key}</em>
                                        </MenuItem>
                                        {Array.isArray(value)
                                          ? value.map((vl) => (
                                            <MenuItem key={vl} value={vl}>
                                              {vl}
                                            </MenuItem>
                                          ))
                                          : value && (
                                            <MenuItem key={value} value={value}>
                                              {value}
                                            </MenuItem>
                                          )}
                                      </Field>
                                    </FormControl>
                                  </Grid>
                                ))}
                              </Grid>
                            </Paper>
                          </Grid>
                        )}

                        {/* Catálogo Virtual e Descrição */}
                        <Grid item xs={12}>
                          <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={Boolean(values.show_in_catalog)}
                                  onChange={(e) => setFieldValue('show_in_catalog', e.target.checked)}
                                  color="primary"
                                />
                              }
                              label={<Typography variant="body2" style={{ fontWeight: 600, color: '#333' }}>Exibir este item no Catálogo Virtual</Typography>}
                            />
                            <UIDuxiaDescriptionButton
                              itemName={values.name}
                              categoryName={values.category ? values.category.name : ''}
                              itemType="produto"
                              currentDescription={values.description}
                              onGenerated={(newDesc) => setFieldValue('description', newDesc)}
                            />
                          </Box>
                          <Field
                            name="description"
                            placeholder="Descrição detalhada do produto para seus clientes visualizarem no catálogo virtual..."
                            multiline
                            rows={3}
                            variant="outlined"
                            size="small"
                            fullWidth
                            as={TextField}
                          />
                        </Grid>

                        {/* Imagens */}
                        <Grid item xs={12}>
                          <UIImageUpload
                            images={values.images || []}
                            onChange={(newImages) => setFieldValue('images', newImages)}
                            label="Fotos do Produto"
                            itemName={values.name}
                          />
                        </Grid>
                      </Grid>
                    </Box>

                    {/* ABA 1: PREÇOS & MARGEM */}
                    <Box style={{ display: tab === 1 ? 'block' : 'none' }}>
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <Paper className={classes.metricCard} elevation={0}>
                            <Grid container spacing={1} alignItems="center">
                              <Grid item xs={12} sm={4}>
                                <Typography variant="caption" color="textSecondary" display="block">
                                  Preço de Venda
                                </Typography>
                                <Typography variant="h6" style={{ fontWeight: 'bold', color: '#17497F' }}>
                                  {formattedCurrency(numValue)}
                                </Typography>
                              </Grid>
                              <Grid item xs={6} sm={4}>
                                <Typography variant="caption" color="textSecondary" display="block">
                                  Margem de Lucro
                                </Typography>
                                <Typography
                                  variant="subtitle1"
                                  className={currentMargin > 20 ? classes.marginHigh : classes.marginLow}
                                >
                                  {currentMargin.toFixed(2)} %
                                </Typography>
                              </Grid>
                              <Grid item xs={6} sm={4}>
                                <Typography variant="caption" color="textSecondary" display="block">
                                  Lucro Estimado ({values.unit || 'UN'})
                                </Typography>
                                <span className={isProfitPositive ? classes.profitBadgePositive : classes.profitBadgeNegative}>
                                  {formattedCurrency(currentProfit)}
                                </span>
                              </Grid>
                            </Grid>
                          </Paper>
                        </Grid>

                        <Grid item xs={12} sm={6} md={3}>
                          <CurrencyTextField
                            type="tel"
                            value={values.cost}
                            label={`Custo por ${values.unit || 'UN'} (R$)`}
                            variant="outlined"
                            size="small"
                            decimalCharacter=","
                            digitGroupSeparator="."
                            currencySymbol="R$"
                            outputFormat="string"
                            error={Boolean(errors.cost)}
                            helperText={touched.cost && errors.cost}
                            onChange={(ev, val) => {
                              setFieldValue('cost', val);
                              setCostTotal(val * (parseFloat(values.amount) || 0));
                              if (values.margin) {
                                const newVal = calculateValueByMargin(val, values.margin);
                                if (newVal) setFieldValue('value', newVal);
                              }
                            }}
                            fullWidth
                          />
                        </Grid>

                        <Grid item xs={12} sm={6} md={3}>
                          <CurrencyTextField
                            type="tel"
                            value={values.value}
                            label={`Preço por ${values.unit || 'UN'} (R$)`}
                            variant="outlined"
                            size="small"
                            decimalCharacter=","
                            digitGroupSeparator="."
                            currencySymbol="R$"
                            outputFormat="string"
                            error={Boolean(errors.value)}
                            helperText={touched.value && errors.value}
                            onChange={(ev, val) => {
                              setFieldValue('value', val);
                              const newMargin = calculateMarginByValue(values.cost, val);
                              setFieldValue('margin', newMargin);
                            }}
                            fullWidth
                          />
                        </Grid>

                        <Grid item xs={12} sm={6} md={3}>
                          <CurrencyTextField
                            type="tel"
                            value={calculateMarginByValue(values.cost, values.value)}
                            label="Margem %"
                            variant="outlined"
                            size="small"
                            decimalCharacter=","
                            digitGroupSeparator="."
                            currencySymbol="%"
                            outputFormat="string"
                            onBlur={(ev, val) => {
                              if (val !== undefined && val !== '') {
                                setFieldValue('margin', val);
                                const newVal = calculateValueByMargin(values.cost, val);
                                if (newVal) setFieldValue('value', newVal);
                              }
                            }}
                            fullWidth
                            helperText="Calcula o valor de venda automaticamente"
                          />
                        </Grid>

                        <Grid item xs={12} sm={6} md={3}>
                          <CurrencyTextField
                            type="tel"
                            value={calculateMarkupByValue(values.cost, values.value)}
                            label="Markup %"
                            variant="filled"
                            size="small"
                            decimalCharacter=","
                            digitGroupSeparator="."
                            currencySymbol="%"
                            outputFormat="string"
                            fullWidth
                            disabled
                          />
                        </Grid>

                        <Grid item xs={12}>
                          <Box
                            p={2}
                            borderRadius={8}
                            bgcolor="#f8fafc"
                            border="1px solid #e2e8f0"
                            display="flex"
                            alignItems="center"
                            gridGap={12}
                          >
                            <span style={{ fontSize: '1.4rem' }}>💡</span>
                            <Typography variant="body2" color="textSecondary">
                              <strong>Dica de Precificação:</strong> Você pode informar o Preço de Venda diretamente ou digitar a Margem de Lucro desejada (%) para que o sistema calcule o preço final sobre o custo unitário.
                            </Typography>
                          </Box>
                        </Grid>
                      </Grid>
                    </Box>

                    {/* ABA 2: ESTOQUE INICIAL */}
                    <Box style={{ display: tab === 2 ? 'block' : 'none' }}>
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <Box className={classes.infoCard}>
                            <Typography variant="subtitle2" style={{ color: '#0d47a1', fontWeight: 600, marginBottom: 4 }}>
                              Saldo de Abertura do Estoque
                            </Typography>
                            <Typography variant="body2" color="textSecondary" style={{ lineHeight: 1.5 }}>
                              Informe a unidade de medida e a quantidade física que você possui atualmente deste produto. Essa quantidade será creditada automaticamente como saldo inicial de estoque ao salvar o produto.
                            </Typography>
                          </Box>
                        </Grid>

                        <Grid item xs={12}>
                          <ProductUnitSelect
                            value={values.unit || 'UN'}
                            onChange={(selectedUnit) => setFieldValue('unit', selectedUnit)}
                            label="Unidade de Medida de Estoque"
                          />
                        </Grid>

                        <Grid item xs={12} sm={4}>
                          <TextField
                            name="amount"
                            label={`Quantidade Inicial (${values.unit || 'UN'})`}
                            type="number"
                            variant="outlined"
                            size="small"
                            error={Boolean(errors.amount)}
                            helperText={touched.amount && errors.amount}
                            placeholder={isFractionalUnit(values.unit) ? "Ex: 1.500" : "0"}
                            onChange={(ev) => {
                              const val = ev.target.value;
                              setFieldValue('amount', val);
                              setCostTotal(val * (parseFloat(values.cost) || 0));
                            }}
                            value={values.amount}
                            fullWidth
                            inputProps={{ min: 0, step: getUnitInputStep(values.unit) }}
                          />
                        </Grid>

                        <Grid item xs={12} sm={4}>
                          <CurrencyTextField
                            decimalPlaces={5}
                            value={values.cost}
                            label={`Custo por ${values.unit || 'UN'} (R$)`}
                            variant="outlined"
                            size="small"
                            decimalCharacter=","
                            error={Boolean(errors.cost)}
                            helperText={touched.cost && errors.cost}
                            digitGroupSeparator="."
                            currencySymbol="R$"
                            outputFormat="string"
                            onChange={(ev, val) => {
                              setFieldValue('cost', val);
                              setCostTotal(val * (parseFloat(values.amount) || 0));
                            }}
                            fullWidth
                          />
                        </Grid>

                        <Grid item xs={12} sm={4}>
                          <CurrencyTextField
                            decimalPlaces={2}
                            value={(parseFloat(values.amount) || 0) * (parseFloat(values.cost) || 0)}
                            label="Custo Total em Estoque"
                            variant="filled"
                            size="small"
                            decimalCharacter=","
                            digitGroupSeparator="."
                            currencySymbol="R$"
                            outputFormat="string"
                            fullWidth
                            disabled
                            helperText="Cálculo automático: Quantidade × Custo Unitário"
                          />
                        </Grid>

                        <Grid item xs={12}>
                          <Box
                            p={2}
                            borderRadius={8}
                            bgcolor="#f8fafc"
                            border="1px solid #e2e8f0"
                            display="flex"
                            alignItems="center"
                            gridGap={12}
                          >
                            <span style={{ fontSize: '1.4rem' }}>💡</span>
                            <Typography variant="body2" color="textSecondary">
                              <strong>Vende fracionado?</strong> Se este item for uma embalagem coletiva (ex.: fardo de cerveja, caixa ou garrafa de 1L) que também será vendida em frações (latas avulsas, doses, porções), mantenha a quantidade total da embalagem principal aqui e adicione os formatos derivados na aba <strong>Formatos de Venda</strong>.
                            </Typography>
                          </Box>
                        </Grid>
                      </Grid>
                    </Box>

                    {/* ABA 3: FORMATOS DE VENDA */}
                    <Box style={{ display: tab === 3 ? 'block' : 'none' }}>
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <Box className={classes.formatHeader} id="tour-product-formats-header">
                            <Box>
                              <Typography variant="subtitle1" style={{ fontWeight: 600, color: '#17497F' }}>
                                Formatos Fracionados & Derivados
                              </Typography>
                              <Typography variant="body2" color="textSecondary" id="tour-product-formats-stock-info">
                                Permite vender doses, porções ou unidades avulsas abatendo proporcionalmente do estoque da embalagem principal.
                              </Typography>
                            </Box>
                            <Button
                              id="tour-product-add-format-btn"
                              variant="contained"
                              color="primary"
                              size="small"
                              startIcon={<AddIcon />}
                              onClick={() => {
                                setEditingFormatIndex(null);
                                setModalFormatOpen(true);
                              }}
                              className={classes.addFormatBtn}
                            >
                              Adicionar Formato
                            </Button>
                          </Box>
                        </Grid>

                        <Grid item xs={12} id="tour-product-formats-container">
                          {saleFormats.length > 0 ? (
                            <Grid container spacing={2}>
                              {saleFormats.map((fmt, idx) => (
                                <Grid item xs={12} sm={6} md={4} key={idx}>
                                  <ProductSaleFormatCard
                                    format={fmt}
                                    onEdit={() => {
                                      setEditingFormatIndex(idx);
                                      setModalFormatOpen(true);
                                    }}
                                    onDelete={() => {
                                      setSaleFormats(saleFormats.filter((_, i) => i !== idx));
                                    }}
                                  />
                                </Grid>
                              ))}
                            </Grid>
                          ) : (
                            <Box
                              p={4}
                              textAlign="center"
                              style={{
                                border: '2px dashed #d0d7de',
                                borderRadius: 8,
                                backgroundColor: '#fafbfc',
                                color: '#6c757d',
                              }}
                            >
                              <Typography variant="subtitle1" style={{ fontWeight: 600, color: '#333' }}>
                                Nenhum formato fracionado cadastrado
                              </Typography>
                              <Typography variant="body2" color="textSecondary" style={{ maxWidth: 480, margin: '8px auto 16px' }}>
                                O produto será vendido exclusivamente na embalagem principal. Se você vende também em doses, copos, fatias ou unidades avulsas, adicione os formatos derivados aqui.
                              </Typography>
                              <Button
                                variant="outlined"
                                color="primary"
                                startIcon={<AddIcon />}
                                onClick={() => {
                                  setEditingFormatIndex(null);
                                  setModalFormatOpen(true);
                                }}
                                style={{ textTransform: 'none', fontWeight: 600 }}
                              >
                                Criar Formato de Venda
                              </Button>
                            </Box>
                          )}
                        </Grid>
                      </Grid>
                    </Box>
                  </Paper>
                </Grid>
              </UIContainer>

              <UIFormContainerSave
                container
                justify="center"
                direction="row"
                messageSucess={registerInfo.data && registerInfo.data.message}
                messageError="Alguma coisa está errada, verifique os campos em destaque"
                error={registerInfo.error}
                loading={isSaving || registerInfo.loading}
                data={registerInfo.data}
                setrequestinfo={setRegisterInfo}
                setFormValues={() => setOldValues(null)}
              />

              <UIModalOldForm
                open={open}
                changeModal={changeModal}
                setFormValues={setFormValues}
              />

              <SupplierCreateModal
                open={modalSupplier}
                initialName={newSupplierName}
                onClose={() => setModalSupplier(false)}
                onSuccess={(newSupplier) => {
                  suppliersLoad();
                  setFieldValue('supplier', newSupplier);
                  setModalSupplier(false);
                }}
              />

              <ProductSaleFormatModal
                open={modalFormatOpen}
                onClose={() => {
                  setModalFormatOpen(false);
                  setEditingFormatIndex(null);
                }}
                onSave={(formatData) => {
                  if (editingFormatIndex !== null) {
                    const updated = [...saleFormats];
                    updated[editingFormatIndex] = formatData;
                    setSaleFormats(updated);
                  } else {
                    setSaleFormats([...saleFormats, formatData]);
                  }
                }}
                initialData={editingFormatIndex !== null ? saleFormats[editingFormatIndex] : null}
                parentCost={parseFloat(values.cost) || 0}
                parentName={values.name}
                parentUnit={values.unit || 'UN'}
                category={values.category}
                categories={categoryInfo.data || []}
              />
            </Form>
          );
        }}
      </Formik>
    </>
  );
};

export default ProductNew;